import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ZoomIn } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { FrameCorners, KnotDivider } from "@/components/ornaments";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FramedImage } from "@/components/FramedImage";
import { isTall } from "@/lib/aspects";
import { useT } from "@/lib/labels";

const KingdomRow = ({ a, i }) => (
  <Link to={`/wiki/${a.slug}`} data-testid={`map-kingdom-${a.slug}`} style={{ animationDelay: `${i * 60}ms` }} className={`group flex gap-4 vellum p-3 rise lift theme-${a.theme || "neutral"}`}>
    <div className="w-20 h-20 shrink-0 border border-[#c9a227] bg-[#ebe2cd] overflow-hidden grid place-items-center">
      {a.infobox_image || a.hero_image ? <img src={imgUrl(a.infobox_image || a.hero_image)} alt="" className="w-full h-full object-cover" />
        : <span className="font-display text-3xl text-[color:var(--accent)]">{a.title.replace(/^(the|a|an)\s+/i, "").charAt(0)}</span>}
    </div>
    <div>
      <p className="font-heading font-bold group-hover:text-[color:var(--accent)] transition-colors">{a.title}</p>
      {a.summary && <p className="text-sm text-[#4a3a2a] line-clamp-2">{a.summary}</p>}
    </div>
  </Link>
);

export default function MapPage() {
  const [d, setD] = useState(null);
  const [zoom, setZoom] = useState(false);
  const t = useT();
  useEffect(() => { api.get("/map").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-center py-32 font-display text-2xl animate-pulse">Unrolling the map…</p>;
  const tall = isTall(d.map_aspect || "portrait");

  return (
    <div data-testid="map-page" className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
      <nav className="text-sm font-heading text-[#5e4a35] mb-8"><Link to="/" className="hover:text-[#991b1b]">Home</Link> <span className="mx-2">›</span> Map</nav>
      {t("map.eyebrow") && <p className="eyebrow theme-frost">{t("map.eyebrow")}</p>}
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2">{d.map_caption || "Map of the Realms"}</h1>
      <div className={tall ? "grid lg:grid-cols-12 gap-10 mt-10 items-start" : "mt-10"}>
        <div className={tall ? "lg:col-span-7" : ""}>
          {d.map_image ? (
            <button data-testid="map-image-button" onClick={() => setZoom(true)} className="group relative block w-full vellum gilt p-3 rise">
              <FrameCorners />
              <FramedImage testid="map-image" src={d.map_image} alt="Map of the realms" aspect={d.map_aspect} fallback="portrait" />
              {t("map.enlarge") && <span className="absolute right-6 bottom-6 inline-flex items-center gap-2 bg-[#1a120c]/85 text-[#f4efe2] px-4 py-2 font-heading text-xs uppercase tracking-[0.2em] opacity-80 group-hover:opacity-100"><ZoomIn className="w-4 h-4" /> {t("map.enlarge")}</span>}
            </button>
          ) : <p className="italic">No map has been uploaded yet.</p>}
        </div>
        <div className={tall ? "lg:col-span-5" : ""}>
          {d.groups.map((g) => (
            <section key={g.section.id} data-testid={`map-group-${g.section.slug}`} className={tall ? "mb-12" : "mt-16"}>
              {!tall && <KnotDivider className="mb-8" />}
              {t("map.guide") && <p className="eyebrow">{t("map.guide")}</p>}
              <h2 className="font-display text-3xl mt-1 mb-6">{g.section.name}</h2>
              {g.articles.length === 0 ? <p className="italic">No kingdoms inscribed yet.</p> : tall ? (
                <div className="space-y-3">{g.articles.map((a, i) => <KingdomRow key={a.id} a={a} i={i} />)}</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{g.articles.map((a, i) => <ArticleCard key={a.id} a={a} index={i} />)}</div>
              )}
            </section>
          ))}
        </div>
      </div>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-[95vw] w-[95vw] bg-[#f4efe2] border-2 border-[#c9a227] rounded-none p-3">
          <DialogTitle className="sr-only">Map</DialogTitle>
          <div className="overflow-auto max-h-[85vh]"><img src={imgUrl(d.map_image)} alt="Map enlarged" className="min-w-[1400px] w-full" /></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
